import { DeliveryHubPage } from './delivery-hub/DeliveryHubPage'
import { QrPaymentSettingsPage } from './qr-payment/QrPaymentSettingsPage'
import { DeliveryStatisticsPage } from './delivery-statistics/DeliveryStatisticsPage'
import { DeliveryZonesPage } from './delivery-zones/DeliveryZonesPage'
import { DispatchPage } from './dispatch/DispatchPage'
import { ExternalDriverPage } from './external-driver/ExternalDriverPage'
import { DriversRedirect } from './drivers/DriversRedirect'
import { TripLogPage } from './trip-log/TripLogPage'
import { VehicleFleetPage } from './vehicle-fleet/VehicleFleetPage'

/**
 * `manager-delivery` — the road: dispatch, drivers, vehicles and zones.
 *
 * Ported: every page.
 *
 * `external-driver` carried the §9.2 diagnosis: the feature was broken because
 * `is_external` had no write path in either backend, so the screen managed a set
 * that was always empty. The screen itself was correct and was ported faithfully;
 * the fix was a BACKEND.md work item, because §9.2 forbids the frontend papering
 * over a broken API. **The backend gained that write path and this said otherwise
 * until 2026-08-05** — see `external-driver/external-driver.ts` for what changed.
 *
 * The zone arithmetic the editor writes is in `@agos/shared` —
 * `findZoneForPoint`, `deliveryFeeCents`, 16 tests — because the order builder
 * and the dispatch board read the same answers.
 */

/**
 * *Rozvoz*'s statistics screen's gate - its route's, and `/drivers`' as well, because that path only redirects
 * there (§9.759, FB-1078).
 *
 * One object rather than two copies, so the redirect cannot come to ask something its destination does not. It
 * asked a right `drivers` - a faithful port of legacy's own check on that word - which the catalogue has never
 * held, so an old bookmark refused everybody but the main administrator instead of redirecting.
 */
const DELIVERY_STATISTICS_GUARD = {
  permission: 'delivery_statistics',
  franchiseModule: 'delivery_statistics',
} as const

export const managerDeliveryModule = {
  name: 'manager-delivery',
  routes: [
    {
      path: '/dispatch',
      page: 'dispatch',
      guard: { permission: 'dispatch' },
      component: DispatchPage,
    },
    {
      // §9.302 - a hub since the owner asked for the QR payment settings to live here.
      //
      // **`delivery_settings` alone - §9.744, owner O114.** It admitted `delivery_settings` *or*
      // `qr_payment` so that somebody holding only the QR right kept a way in after the fold, and that
      // is the owner's report: *"Práva na nastavenie rozvoz aj keď sú vypnuté vie nastavovať qr
      // platby"* - un-ticking *Nastavenia rozvozu* left this hub in the sidebar, opening on the QR form.
      // The QR form lives inside *Nastavenia rozvozu* now, so its tab adds `qr_payment` to this right
      // rather than standing in for it. No tier gate, for §9.302's reason: the two tabs have different
      // modules, and each tab carries its own.
      path: '/delivery-settings',
      page: 'delivery-settings',
      guard: { permission: 'delivery_settings' },
      component: DeliveryHubPage,
    },
    {
      // Moved here from `manager-invoicing` by §9.302, path unchanged: a hub is not a wall in front
      // of a page somebody has bookmarked, and `qr-payment.spec.ts` drives this path.
      //
      // **§9.744 - the deep link admits whoever the QR tab inside the hub admits**: the hub's
      // `delivery_settings`, the tab's own `qr_payment` and the tab's `qr_payment` module, which this
      // route lacked until now. The guard grammar has no *all of*, and needs none:
      // `module-contract.ts`' gates are independent and all of them must pass, so `permission` and
      // `anyPermission` are two required gates and an `anyPermission` of one key is that key,
      // required. The refusal names whichever of the two is missing.
      path: '/qr-payment',
      page: 'qr-payment-settings',
      guard: {
        permission: 'qr_payment',
        anyPermission: ['delivery_settings'],
        franchiseModule: 'qr_payment',
      },
      component: QrPaymentSettingsPage,
    },
    {
      // Folded into the statistics screen; legacy kept the path as a redirect. §9.759 - it asks what the
      // screen it lands on asks.
      path: '/drivers',
      page: 'drivers',
      guard: DELIVERY_STATISTICS_GUARD,
      redirectTo: '/delivery-statistics',
      component: DriversRedirect,
    },
    {
      path: '/external-driver',
      page: 'external-driver',
      guard: { permission: 'external_driver', franchiseModule: 'external_driver' },
      component: ExternalDriverPage,
    },
    {
      path: '/delivery-statistics',
      page: 'delivery-statistics',
      guard: DELIVERY_STATISTICS_GUARD,
      component: DeliveryStatisticsPage,
    },
    {
      path: '/delivery-zones',
      page: 'delivery-zones',
      guard: { permission: 'delivery_zones' },
      component: DeliveryZonesPage,
    },
    {
      path: '/trip-log',
      page: 'trip-log',
      guard: { permission: 'trip_log', franchiseModule: 'trip_log' },
      component: TripLogPage,
    },
    {
      path: '/vehicle-fleet',
      page: 'vehicle-fleet',
      guard: { permission: 'vehicle_fleet', franchiseModule: 'vehicle_fleet' },
      component: VehicleFleetPage,
    },
  ],
} as const

export {
  DeliveryHubPage,
  DeliveryStatisticsPage,
  DeliveryZonesPage,
  DispatchPage,
  DriversRedirect,
  ExternalDriverPage,
  QrPaymentSettingsPage,
  TripLogPage,
  VehicleFleetPage,
}

// §9.302 - the app's fold test reads these to assert the tabs admit exactly whoever the sidebar
// entries they replaced admitted.
export { DELIVERY_TABS } from './delivery-hub/delivery-hub'

// §9.674, owner O58 - the shell draws a red count of the board's unaccepted orders on its own nav entry.
// The read and the rule stay here, beside the board whose first band they count.
export { useUnacceptedCount } from './dispatch/use-unaccepted-count'
